// VEDEREA ÎNTREGII CLĂDIRI pe pagina Instalații: clădirea translucidă
// (`cladire3d`) și toate instalațiile ei deodată, de la racordul străzii la
// ventilația de peste acoperiș — ca macheta structurii din Studio. Sistemele
// se aprind și se sting din aceleași comutatoare ca pe nivel, clicul pe o
// țeavă o alege (panoul îi arată datele și ruta), iar comenzile de cameră
// sunt cele ale tuturor machetelor (`comenzi3d`: rotiță, trackpad, taste).

import { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { legComenzi3D, primitiveOrbita, schemaMouse } from "../comenzi3d";
import { useT } from "../i18n";
import type { CanonicalDoc } from "../viewer/canonical";
import { toStoreyPlans } from "../viewer/canonical";
import { lumeDreapta, razaInDocument } from "../viewer/plan/oglinda3d";
import { construiesteCladire } from "./cladire3d";
import { useInstalatii } from "./context";
import { inCadruLocal } from "./dinDocument";
import { construiesteStrat, type StratInstalatii } from "./strat3d";
import "./instalatii.css";

/** Cât de aproape de o țeavă subțire poate cădea clicul (în metri, în
 *  cadrul documentului) ca s-o aleagă: pe toată clădirea țevile au un pixel. */
const TOLERANTA_CLIC_M = 0.12;

export default function MachetaCladire({ doc }: { doc: CanonicalDoc }) {
  const t = useT();
  const inst = useInstalatii();
  const gazda = useRef<HTMLDivElement>(null);
  const docLocal = useMemo(() => inCadruLocal(doc), [doc]);
  const plansee = useMemo(() => toStoreyPlans(docLocal), [docLocal]);
  const model = inst?.model ?? null;
  const api = useRef<{ strat: StratInstalatii; deseneaza: () => void } | null>(null);
  const alegeRef = useRef(inst?.alege);
  alegeRef.current = inst?.alege;

  useEffect(() => {
    const el = gazda.current;
    if (!el || !model) return;
    const rand = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    rand.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    el.appendChild(rand.domElement);
    const scena = new THREE.Scene();
    const lume = lumeDreapta();
    scena.add(lume);
    const cladire = construiesteCladire(plansee, docLocal);
    lume.add(cladire.grup);
    const strat = construiesteStrat(model, { cote: new Map(plansee.map((p) => [p.id, p.cota ?? 0])) });
    lume.add(strat.grup);
    scena.add(new THREE.HemisphereLight(0xffffff, 0x9a948a, 1.1));
    const soare = new THREE.DirectionalLight(0xffffff, 0.7);
    soare.position.set(80, -120, 160);
    scena.add(soare);

    const cam = new THREE.PerspectiveCamera(42, 1, 0.5, 4000);
    cam.up.set(0, 0, 1);
    const ctrl = new OrbitControls(cam, rand.domElement);
    schemaMouse(ctrl, true);
    let cerut = false;
    const deseneaza = () => {
      if (cerut) return;
      cerut = true;
      requestAnimationFrame(() => { cerut = false; rand.render(scena, cam); });
    };
    // încadrarea: cutia clădirii, în lume (grupul întors pe y)
    const cutie = new THREE.Box3().setFromObject(lume);
    const mij = cutie.getCenter(new THREE.Vector3());
    const dim = cutie.getSize(new THREE.Vector3());
    const raza = Math.max(dim.x, dim.y, dim.z, 10);
    const reseteaza = () => {
      cam.position.set(mij.x + raza * 0.8, mij.y - raza * 0.9, mij.z + raza * 0.6);
      ctrl.target.copy(mij);
      cam.lookAt(mij);
      ctrl.update();
      deseneaza();
    };
    reseteaza();
    const desfaComenzi = legComenzi3D(rand.domElement, primitiveOrbita(cam, ctrl, el, { reseteaza, dupa: deseneaza }));
    ctrl.addEventListener("change", deseneaza);
    const obs = new ResizeObserver(() => {
      const w = el.clientWidth, h = el.clientHeight;
      if (!w || !h) return;
      rand.setSize(w, h, false);
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
      deseneaza();
    });
    obs.observe(el);

    // clicul (nu tragerea): țeava de sub cursor sau cea mai apropiată de rază
    const ray = new THREE.Raycaster();
    const nd = new THREE.Vector2();
    let jos: [number, number] | null = null;
    const laApasare = (ev: PointerEvent) => { jos = [ev.clientX, ev.clientY]; };
    const laRidicare = (ev: PointerEvent) => {
      if (!jos || Math.hypot(ev.clientX - jos[0], ev.clientY - jos[1]) > 4) return;
      const r = rand.domElement.getBoundingClientRect();
      nd.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
      ray.setFromCamera(nd, cam);
      const lovit = ray.intersectObject(strat.grup, true).find((h) => h.object.visible);
      const id = (lovit && strat.alege(lovit)) || strat.alegeAproape(razaInDocument(ray.ray.clone()), TOLERANTA_CLIC_M);
      alegeRef.current?.(id);
    };
    rand.domElement.addEventListener("pointerdown", laApasare);
    rand.domElement.addEventListener("pointerup", laRidicare);
    api.current = { strat, deseneaza };
    deseneaza();
    return () => {
      api.current = null;
      rand.domElement.removeEventListener("pointerdown", laApasare);
      rand.domElement.removeEventListener("pointerup", laRidicare);
      obs.disconnect();
      desfaComenzi();
      ctrl.dispose();
      strat.elibereaza();
      cladire.elibereaza();
      rand.dispose();
      rand.domElement.remove();
    };
  }, [model, plansee, docLocal]);

  useEffect(() => {
    if (!inst || !api.current) return;
    api.current.strat.arata(inst.vizibile);
    api.current.deseneaza();
  }, [inst, inst?.vizibile]);
  useEffect(() => {
    if (!inst || !api.current) return;
    api.current.strat.evidentiaza(new Set([...(inst.ales ? [inst.ales] : []), ...inst.evidentiate]));
    api.current.deseneaza();
  }, [inst, inst?.ales, inst?.evidentiate]);
  useEffect(() => {
    if (!inst || !api.current) return;
    api.current.strat.arataApartamente(inst.apartamente);
    api.current.strat.estompeaza(inst.doarApartamentul && inst.apartamentAles
      ? new Set(inst.model.segmente.filter((s) => s.flat === inst.apartamentAles || !s.flat).map((s) => s.id)
                  .concat(inst.model.echipamente.filter((e) => e.flat === inst.apartamentAles || !e.flat).map((e) => e.id)))
      : null);
    api.current.deseneaza();
  }, [inst, inst?.apartamente, inst?.doarApartamentul, inst?.apartamentAles]);

  return (
    <div className="inst-cladire-scena" ref={gazda} title={t("inst.cladire.sugestie")} />
  );
}
